import { LoginError, UnauthenticatedError } from '../errors';
import { logger } from '../utils/logger';
import { isSDKUserToken } from '../utils/sdk_user';
export const ACCESS_STORAGE_KEY = 'STREEM_ACCESS_TOKEN';
let localAccessToken = null;
// Expire the access token a few seconds earlier to account for network+code latency.
const TokenExpirationSlop = 10;
export class AccessTokenManager {
    constructor(apiUrl, appId) {
        this.apiUrl = apiUrl;
        this.appId = appId;
    }
    getExistingTokenIfValid() {
        const storedToken = this.retrieveFromStorage();
        if (storedToken && storedToken.expiresAt > Date.now() / 1000) {
            logger.debug('Found a stored access token that is still valid');
            return storedToken.token;
        }
    }
    async fetch(idOrSdkToken, companyCode) {
        // Attempt to retrieve a cached access token from storage.
        const existingToken = this.getExistingTokenIfValid();
        if (existingToken)
            return existingToken;
        const storedToken = this.retrieveFromStorage();
        if (storedToken) {
            logger.info('Found a stored access token but it has expired, so refreshing it');
            try {
                return await this.refresh(companyCode);
            }
            catch (error) {
                // SDK Tokens (aka Embedded SSO) doesn't support Refresh Tokens, so info() in that case, otherwise error()
                const loggerMethod = isSDKUserToken(idOrSdkToken) ? 'info' : 'error';
                logger[loggerMethod](`Access token is expired and we were unable to refresh the token for company code: ${companyCode}, user id: ${storedToken.token.streem_user_id}`, error);
            }
        }
        logger.info('Could not find a stored access token, so requesting one from /auth/token');
        let scope = 'streem:user';
        if (isSDKUserToken(idOrSdkToken)) {
            scope += ' streem:sdk';
        }
        const oauthCredentials = await this.requestAccessToken({
            assertion: idOrSdkToken,
            grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
            scope,
            client_id: this.appId,
            streem_company_code: companyCode,
            audience: `${this.apiUrl}/`,
        });
        this.storeAccessToken(oauthCredentials);
        return oauthCredentials;
    }
    /**
     * Requests a new token using the current token's `refresh_token`
     */
    async refresh(companyCode) {
        const storedOldToken = this.retrieveFromStorage();
        if (!storedOldToken || !storedOldToken.token.refresh_token) {
            throw new Error('Failed to refresh token: credentials do not exist in storage');
        }
        let accessToken = await this.requestAccessToken({
            refresh_token: storedOldToken.token.refresh_token,
            grant_type: `refresh_token`,
            client_id: this.appId,
            streem_company_code: companyCode,
        });
        // The new token we receive when using a refresh token
        // might not have a new refresh_token. If it does not have
        // a new refresh token, we should use the old token
        accessToken = {
            ...accessToken,
            refresh_token: accessToken.refresh_token ?? storedOldToken.token.refresh_token,
        };
        this.storeAccessToken(accessToken);
        return accessToken;
    }
    /**
     * Removes token from session storage
     */
    clearStorage() {
        logger.info('Removing access token from sessionStorage');
        sessionStorage.removeItem(ACCESS_STORAGE_KEY);
        localAccessToken = null;
    }
    retrieveFromStorage() {
        let rawAccessToken = sessionStorage.getItem(ACCESS_STORAGE_KEY);
        if (!rawAccessToken) {
            if (localAccessToken) {
                logger.warn('Unable to find access token in sessionStorage, but did find in local variable... using local variable');
                rawAccessToken = localAccessToken;
            }
            else {
                return;
            }
        }
        try {
            return JSON.parse(rawAccessToken);
        }
        catch (error) {
            // We failed to parse the JSON, which means our token is corrupted.
            logger.error('Failed to parse access token from SessionStorage.', error);
            this.clearStorage();
            return;
        }
    }
    /**
     * Stores a token in session storage
     * @param accessToken The token to be stored
     */
    storeAccessToken(accessToken) {
        const token = JSON.stringify({
            expiresAt: Date.now() / 1000 + accessToken.expires_in - TokenExpirationSlop,
            token: accessToken,
        });
        sessionStorage.setItem(ACCESS_STORAGE_KEY, token);
        localAccessToken = token;
    }
    /**
     * Requests access_token
     * @param body PostOAuthCredentialsBody payload
     */
    async requestAccessToken(body) {
        const bodyParams = new URLSearchParams();
        for (const key in body) {
            if (Object.prototype.hasOwnProperty.call(body, key)) {
                const value = body[key];
                if (value) {
                    bodyParams.append(key, value);
                }
            }
        }
        const response = await fetch(`${this.apiUrl}/auth/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: bodyParams,
        });
        const { status } = response;
        if (status >= 200 && status < 300) {
            const accessToken = await response.json();
            return accessToken;
        }
        else if (status >= 400 && status < 500) {
            throw new UnauthenticatedError(`Received error.status=${response.status} error.responseBody=<${await response.text()}>  error.url=<${response.url}>`, response.status);
        }
        else {
            throw new LoginError(`Received error error.status=${response.status} error.responseBody=<${await response.text()}>  error.url=<${response.url}>`, response.status);
        }
    }
    async invitationLogin(inviteCode) {
        return await this.getInvitationAccessToken(inviteCode);
    }
    async getInvitationAccessToken(inviteCode) {
        const response = await fetch(`${this.apiUrl}/v1/auth/invitation`, {
            method: 'POST',
            body: JSON.stringify({
                invitation_code: inviteCode,
                app_sid: this.appId,
                resource: this.apiUrl,
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        logger.info(`Attempted to fetch access token using an invitation code, status: ${response.status}`);
        if (!response.ok) {
            switch (response.status) {
                case 401:
                    throw new LoginError('Invalid login', response.status);
                case 403:
                    throw new LoginError('Referrer header does not match', response.status);
                default:
                    throw new LoginError('Unknown authentication error.', response.status);
            }
        }
        const invitation = await response.json();
        logger.info(`Successfully received invitation access token.  Attempting to store in sessionStorage (available: ${storageAvailable('sessionStorage')})`);
        this.storeAccessToken(invitation);
        const retrievedValue = this.retrieveFromStorage();
        const rawValue = sessionStorage.getItem(ACCESS_STORAGE_KEY);
        logger.info(`After storing invitation access token in sessionStorage, raw value is ` +
            `type: ${typeof rawValue}, token value is type: ${typeof retrievedValue}`);
        return invitation;
    }
}
// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#testing_for_availability
function storageAvailable(type) {
    let storage;
    try {
        storage = window[type];
        const x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    }
    catch (e) {
        return (e instanceof DOMException &&
            // everything except Firefox
            (e.code === 22 ||
                // Firefox
                e.code === 1014 ||
                // test name field too, because code might not be present
                // everything except Firefox
                e.name === 'QuotaExceededError' ||
                // Firefox
                e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            // acknowledge QuotaExceededError only if there's something already stored
            storage &&
            storage.length !== 0);
    }
}
//# sourceMappingURL=access_token_manager.js.map